import React, { useEffect, useState } from "react";
import ModalWrap from "./ModalWrap";
import { Checkbox, FormControlLabel, Paper, Typography } from "@mui/material";
import "./summaryreportpdf.scss";
import closeIcon from "../../assets/close-icon.svg";
import { setLoading } from "../state/action/globalAction";
import {
  getRecipients,
  getLoggedInUserDetails,
  sendPdf,
  createRecipient,
  updateRecipient,
  deleteRecipient,
} from "../../services/romReports";
import { useDispatch, useSelector } from "react-redux";
import { pageLocale } from "../../utils/locale";
import { getParams } from "../../utils/helpers";
import { FRONT_TUG_V2_PDF, SIDE_TUG_V2_PDF } from "../../routes/routerPath";

const PencilIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
      fill="currentColor"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
      fill="currentColor"
    />
  </svg>
);

const EMPTY_FORM = { firstName: "", lastName: "", email: "" };

const normalizeEmail = (email) => (email || "").trim().toLowerCase();

const isCurrentUserEmail = (email, currentUserEmail) =>
  normalizeEmail(email) &&
  normalizeEmail(email) === normalizeEmail(currentUserEmail);

const isRecipientSelectable = (recipient, currentUserEmail) =>
  !!recipient?.verifiedAt ||
  isCurrentUserEmail(recipient?.email, currentUserEmail);

const RecipientForm = ({
  editForm,
  setEditForm,
  onSave,
  onCancel,
  onDelete,
  formLoading,
  formError,
  labels,
}) => (
  <div className="recipient-form">
    <input
      className="recipient-form__field"
      placeholder={labels.first_name}
      value={editForm.firstName}
      onChange={(e) =>
        setEditForm((f) => ({ ...f, firstName: e.target.value }))
      }
    />
    <input
      className="recipient-form__field"
      placeholder={labels.last_name}
      value={editForm.lastName}
      onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
    />
    <input
      className="recipient-form__field"
      placeholder={labels.email}
      type="email"
      value={editForm.email}
      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
    />
    {formError && (
      <Typography className="recipient-form__error">{formError}</Typography>
    )}
    <div className="recipient-form__actions">
      <button
        className="recipient-form__cancel"
        onClick={onCancel}
        disabled={formLoading}
      >
        {labels.cancel}
      </button>
      <button
        className="recipient-form__save"
        onClick={onSave}
        disabled={
          formLoading || !editForm.firstName.trim() || !editForm.email.trim()
        }
      >
        {labels.save}
      </button>
    </div>
    {onDelete && (
      <button
        className="recipient-form__delete"
        onClick={onDelete}
        disabled={formLoading}
      >
        {labels.delete_recipient}
      </button>
    )}
  </div>
);

const ShareReport = ({
  shareReport,
  setShareReport,
  setSuccessPopup,
  setOpenErrorAlert,
}) => {
  const {
    share_report,
    choose_report_section,
    choose_recipients,
    send_report,
    report_summary,
    exercises_recommended,
    test_history,
    add_recipient,
    first_name,
    last_name,
    email,
    cancel,
    save,
    pending_verification,
    email_already_exists,
    generic_error,
    delete_recipient,
  } = pageLocale("rompdf");
  const [chooseReport, setChooseReport] = useState([
    { value: report_summary, page: "0", flag: false },
    { value: exercises_recommended, page: "1", flag: false },
    { value: test_history, page: "2", flag: false },
  ]);
  const { errorCode } = pageLocale("common");
  const { moduleId } = getParams();
  const [destinationData, setDestinationData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState("");

  const mapApiError = (err) => {
    if (err?.response?.status === 409) {
      return email_already_exists;
    }
    return err?.response?.data?.message || generic_error;
  };
  const isLoading = useSelector((state) => state.global.loading);
  const dispatch = useDispatch();

  useEffect(() => {
    let isMounted = true;
    dispatch(setLoading(true));
    Promise.all([
      getRecipients(),
      getLoggedInUserDetails().catch(() => ({ data: { data: null } })),
    ])
      .then(([res, userRes]) => {
        if (!isMounted) return;
        const userEmail = userRes?.data?.data?.email || "";
        const recipients = (res?.data?.data || []).map((r) => ({
          ...r,
          flag: isCurrentUserEmail(r.email, userEmail),
        }));
        setCurrentUserEmail(userEmail);
        setDestinationData(recipients);
        setShareReport(true);
        dispatch(setLoading(false));
      })
      .catch(() => {
        if (!isMounted) return;
        dispatch(setLoading(false));
      });
    return () => {
      isMounted = false;
    };
  }, [dispatch, setShareReport]);

  const changeRecipients = (event, i) => {
    if (editingIndex !== null || isAdding) return;
    if (!isRecipientSelectable(destinationData[i], currentUserEmail)) return;
    const flag = event.target.checked;
    destinationData[i]["flag"] = flag;
    setDestinationData([...destinationData]);
  };

  const chooseReportHandler = (val, index) => {
    chooseReport[index].flag = val.target.checked;
    setChooseReport([...chooseReport]);
  };

  const startEdit = (index) => {
    const recipient = destinationData[index];
    setEditForm({
      firstName: recipient.firstname || "",
      lastName: recipient.lastname || "",
      email: recipient.email || "",
    });
    setEditingIndex(index);
    setIsAdding(false);
    setFormError(null);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setIsAdding(false);
    setEditForm(EMPTY_FORM);
    setFormError(null);
  };

  const saveEdit = () => {
    const { firstName, lastName, email: emailVal } = editForm;
    const recipient = destinationData[editingIndex];
    setFormLoading(true);
    setFormError(null);
    updateRecipient({
      id: recipient.id,
      firstname: firstName,
      lastname: lastName,
      email: emailVal,
    })
      .then((res) => {
        const updated = [...destinationData];
        const updatedRecipient = {
          ...recipient,
          ...(res?.data?.data || {}),
          firstname: firstName,
          lastname: lastName,
          email: emailVal,
        };
        const selectable = isRecipientSelectable(
          updatedRecipient,
          currentUserEmail,
        );
        updated[editingIndex] = {
          ...updatedRecipient,
          flag:
            selectable &&
            (recipient.flag || isCurrentUserEmail(emailVal, currentUserEmail)),
        };
        setDestinationData(updated);
        cancelEdit();
      })
      .catch((err) => {
        setFormError(mapApiError(err));
      })
      .finally(() => setFormLoading(false));
  };

  const handleDelete = (index = editingIndex) => {
    if (index === null || index === undefined) return;
    const recipient = destinationData[index];
    setFormLoading(true);
    setFormError(null);
    deleteRecipient(recipient.id)
      .then(() => {
        setDestinationData(destinationData.filter((_, i) => i !== index));
        if (index === editingIndex) cancelEdit();
      })
      .catch((err) => {
        setFormError(mapApiError(err));
      })
      .finally(() => setFormLoading(false));
  };

  const startAdd = () => {
    setIsAdding(true);
    setEditingIndex(null);
    setEditForm(EMPTY_FORM);
    setFormError(null);
  };

  const saveAdd = () => {
    const { firstName, lastName, email: emailVal } = editForm;
    setFormLoading(true);
    setFormError(null);
    createRecipient({
      firstname: firstName,
      lastname: lastName,
      email: emailVal,
    })
      .then((res) => {
        const newRecipient = {
          ...(res?.data?.data || {
            id: Date.now().toString(),
            firstname: firstName,
            lastname: lastName,
            email: emailVal,
            verifiedAt: null,
          }),
        };
        newRecipient.flag = isCurrentUserEmail(
          newRecipient.email,
          currentUserEmail,
        );
        setDestinationData([...destinationData, newRecipient]);
        cancelEdit();
      })
      .catch((err) => {
        setFormError(mapApiError(err));
      })
      .finally(() => setFormLoading(false));
  };

  const sendRequest = () => {
    dispatch(setLoading(true));
    const checkList = [];
    for (let k = 0; k < destinationData.length; k++) {
      if (destinationData[k].flag) {
        checkList.push({
          recepientType: destinationData[k].id,
          value: destinationData[k].email,
        });
      }
    }
    let pages = "";
    for (let n = 0; n < chooseReport.length; n++) {
      if (chooseReport[n].flag) {
        pages += chooseReport[n].page;
      }
    }

    const buildReportURL = (pathname) => {
      const url = new URL(window.location.href);
      url.pathname = pathname;
      if (pages) {
        url.searchParams.set("pages", pages);
      }
      return url.toString();
    };

    const mod = Number(moduleId);
    const pdfPath =
      mod === 4 ? FRONT_TUG_V2_PDF : mod === 1 ? SIDE_TUG_V2_PDF : null;
    const printURL = pdfPath
      ? buildReportURL(pdfPath)
      : new URL(window.location.href).toString();

    const request = {
      action: "email",
      printURL,
      module: Number(moduleId),
      recepients: checkList,
    };
    sendPdf(request)
      .then(() => {
        dispatch(setLoading(false));
        setSuccessPopup(true);
        setShareReport(false);
      })
      .catch((err) => {
        dispatch(setLoading(false));
        if (err.response?.status === 401 || err.response?.status === 403) {
          setOpenErrorAlert({ flag: true, text: errorCode });
        } else {
          setOpenErrorAlert({ flag: true, text: err.response?.data?.message });
        }
        setShareReport(false);
      });
  };

  const formLabels = {
    first_name,
    last_name,
    email,
    cancel,
    save,
    delete_recipient,
  };

  return (
    <>
      {!isLoading && (
        <ModalWrap open={shareReport} onClose={() => setShareReport(false)}>
          <div className="report-popup-wrap">
            <div className="header-block">
              <Typography className="page-title">{share_report}</Typography>
              <img
                src={closeIcon}
                alt="closeIcon"
                className="closebtn"
                onClick={() => setShareReport(false)}
              />
            </div>
            <div className="content-wrapper">
              <Paper className="choose-report-wrapper" elevation={3}>
                <Typography className="title">
                  {choose_report_section}
                </Typography>
                {chooseReport.map((i, index) => {
                  return (
                    <Paper
                      key={index}
                      className={`items ${i.flag ? `selected` : ""}`}
                      elevation={2}
                    >
                      <FormControlLabel
                        className="item-name"
                        control={<Checkbox />}
                        label={i.value}
                        onChange={(e) => chooseReportHandler(e, index)}
                      />
                    </Paper>
                  );
                })}
              </Paper>
              <Paper className="report-select-wrap" elevation={3}>
                <Typography className="title">{choose_recipients}</Typography>
                {destinationData?.map((i, index) => {
                  if (editingIndex === index) {
                    return (
                      <Paper key={index} className="item" elevation={2}>
                        <RecipientForm
                          editForm={editForm}
                          setEditForm={setEditForm}
                          formLoading={formLoading}
                          formError={formError}
                          labels={formLabels}
                          onSave={saveEdit}
                          onCancel={cancelEdit}
                          onDelete={handleDelete}
                        />
                      </Paper>
                    );
                  }
                  const displayName =
                    [i.firstname, i.lastname].filter(Boolean).join(" ") ||
                    i.email;
                  const selectable = isRecipientSelectable(i, currentUserEmail);
                  return (
                    <Paper
                      className={`item ${i.flag ? `selected` : ""} ${
                        !selectable ? "disabled" : ""
                      }`}
                      key={index}
                      elevation={2}
                    >
                      <div className="item-row">
                        <FormControlLabel
                          className="item-name"
                          control={
                            <Checkbox
                              onChange={(e) => changeRecipients(e, index)}
                              checked={!!i.flag}
                              disabled={!selectable}
                            />
                          }
                          disabled={!selectable}
                          label={displayName}
                        />
                        <div className="row-actions">
                          <button
                            className="edit-icon-btn"
                            onClick={() => startEdit(index)}
                            aria-label="Edit recipient"
                          >
                            <PencilIcon />
                          </button>
                          <button
                            className="edit-icon-btn"
                            onClick={() => handleDelete(index)}
                            aria-label={delete_recipient}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      </div>
                      <Typography className="email-val">{i.email}</Typography>
                      {!i.verifiedAt && (
                        <Typography className="pending-badge">
                          {pending_verification}
                        </Typography>
                      )}
                    </Paper>
                  );
                })}
                {isAdding ? (
                  <Paper className="item" elevation={2}>
                    <RecipientForm
                      editForm={editForm}
                      setEditForm={setEditForm}
                      formLoading={formLoading}
                      formError={formError}
                      labels={formLabels}
                      onSave={saveAdd}
                      onCancel={cancelEdit}
                    />
                  </Paper>
                ) : (
                  <button
                    className="add-recipient-btn"
                    onClick={startAdd}
                    disabled={editingIndex !== null}
                  >
                    {add_recipient}
                  </button>
                )}
              </Paper>
            </div>
            <div className="modal-footer">
              <hr />
              <button
                className="send-btn"
                onClick={() => sendRequest()}
                disabled={
                  !chooseReport.some((j) => j.flag) ||
                  !destinationData?.some((k) => k.flag)
                }
              >
                {send_report}
              </button>
            </div>
          </div>
        </ModalWrap>
      )}
    </>
  );
};

export default ShareReport;
